import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useOutsideClick,
  Flex,
  Box,
  useDisclosure,
  Icon,
  Badge,
  InputGroup,
  InputLeftElement,
  Input,
} from '@chakra-ui/react';
import { Tag, TagLabel, TagLeftIcon } from '@chakra-ui/react';
import { useState, useRef, useEffect } from 'react';
import { Health } from '@/assets/icons/bold';
import { useDispatch, useSelector } from 'react-redux';
import { applyFilter, clearFilter } from '@/store/outreach';
import { selectOutreachFilter } from '@/store/outreach/selectors';
import { useSearchList } from '@/hooks/use-search-list';
import { debounce, groupBy } from 'lodash-es';
import SearchIcon from '@/icons/Search';
import {
  stages,
  stageGroup as stageGroupList,
} from '@/constants/contact-stages';
import { KText } from '@/ui-library/components/text';
import { FilterFooter } from '@/components/filter';
import { Check } from '@kula-ai/icons-core';

export const StageList = ({
  list = [],
  stageGroupList = [],
  selected = new Set(),
  onSelect = () => {},
  type = 'filterable',
}) => {
  if (list?.length === 0)
    return (
      <KText
        variant="muted"
        textAlign="center"
        mt="6px"
        color="secondary-700"
        size="sm-medium"
      >
        No matching stage found.
      </KText>
    );

  return (
    <>
      {list?.map?.((listItem, i) => {
        return (
          <ul key={`stageGroup-${i}`}>
            <li>
              <KText
                p="sm"
                size="sm-semi-bold"
                color="secondary-900"
                textTransform="capitalize"
              >
                {stageGroupList[listItem[0]]?.label}
              </KText>
            </li>
            {listItem?.[1]?.map((stage, i) => {
              if (!stage?.[type]) return null;
              const isSelected = selected.has(stage?.id);
              return (
                <li key={stage.id} data-test-id={stage?.id}>
                  <Flex
                    alignItems="center"
                    px="8px"
                    borderRadius="8px"
                    _hover={{ bg: 'secondary-50' }}
                  >
                    <Box
                      width="6px"
                      height="6px"
                      bg={stage?.color}
                      borderRadius="full"
                      marginRight="2px"
                    />
                    <Box
                      flex={1}
                      key={i}
                      p="sm"
                      _hover={{
                        bg: 'secondary-50',
                        borderRadius: '5px',
                        fontWeight: 650,
                      }}
                      cursor="pointer"
                      borderRadius="sm"
                      textTransform="capitalize"
                      onClick={() => onSelect(stage)}
                    >
                      <KText
                        size={isSelected ? 'sm-semi-bold' : 'sm-medium'}
                        color={isSelected ? 'primary-500' : 'secondary-700'}
                      >
                        {stage.label}
                      </KText>
                    </Box>
                    {isSelected && (
                      <Icon
                        as={Check}
                        color="primary-500"
                        data-test-id="stage-selected"
                      />
                    )}
                  </Flex>
                </li>
              );
            })}
          </ul>
        );
      })}
    </>
  );
};

export const Stage = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectOutreachFilter);
  const [selected, setSelected] = useState(new Set());
  const { onClose, isOpen, onToggle } = useDisclosure();
  const ref = useRef(null);
  const userInput = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredList = useSearchList({
    list: stages,
    searchTerm: searchTerm,
    key: 'label',
  });

  const stagesList = groupBy(filteredList, 'group');
  const list = Object.entries(stagesList);

  const OnSearchUser = debounce(() => {
    setSearchTerm(userInput?.current?.value);
  }, 300);

  const onClick = (stage) => {
    setSelected((currStage) => {
      const stageId = stage?.id;
      if (currStage.has(stageId)) {
        currStage.delete(stageId);
        return new Set(currStage);
      }
      currStage.add(stageId);
      return new Set(currStage);
    });
  };

  const resetStage = () => {
    dispatch(clearFilter({ type: 'stages', isMulti: true }));
    onClose();
  };

  const applyStageFilter = () => {
    dispatch(applyFilter({ type: 'stages', value: [...selected] }));
    onClose();
  };

  useOutsideClick({
    ref: ref,
    handler: () => onClose(),
  });

  useEffect(() => {
    const { stages: selectedStages } = filters;
    setSelected(new Set(selectedStages));
  }, [filters]);

  return (
    <Box ref={ref} mr="12px">
      <Popover placement="bottom-start" isOpen={isOpen}>
        <PopoverTrigger>
          <Tag
            height="40px"
            bg={isOpen ? '#F6F7F8' : '#FFF'}
            borderRadius="full"
            borderWidth="1px"
            borderColor="light.border"
            _hover={{ background: 'light.hover' }}
            cursor="pointer"
            px="16px"
            onClick={onToggle}
          >
            <TagLeftIcon
              as={Health}
              set="bold"
              width="16px"
              height="16px"
              color="#FF9900"
            />
            <TagLabel fontSize="sm" fontWeight="600">
              Stage
            </TagLabel>
            {filters?.stages?.length > 0 && (
              <Badge
                width="20px"
                height="20px"
                bg="#131415"
                color="white"
                textAlign="center"
                pt="2px"
                pl="2px"
                ml="4px"
                borderRadius="50%"
              >
                {filters?.stages?.length}
              </Badge>
            )}
          </Tag>
        </PopoverTrigger>

        <PopoverContent width="300px">
          <PopoverBody padding="8px">
            <Box px="8px" mb="8px">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon />
                </InputLeftElement>
                <Input
                  placeholder="Search Stage"
                  onChange={OnSearchUser}
                  ref={userInput}
                />
              </InputGroup>
            </Box>
            <Box minHeight="45px" maxHeight="200px" overflow="auto">
              <StageList
                list={list}
                stageGroupList={stageGroupList}
                selected={selected}
                onSelect={onClick}
              />
            </Box>
            <FilterFooter
              selected={Array?.from(selected)}
              onApply={applyStageFilter}
              onClear={resetStage}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
